import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical'
import {
  ActionIcon,
  Box,
  type BoxProps,
  Card,
  Menu,
  Text,
  ThemeIcon,
} from '@mantine/core'

import {
  useMutationUpdateUserAddress,
  useQueryGetUserAddressById,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserAddress } from '@flecto/types'
import { Badge, Icon } from '@flecto/ui'
import { TextAddress } from '@flecto/ui/@shared'
import { useAddressCountryFlag } from '@flecto/utils'

import { useAddressDrawer } from '../../hooks/useAddressDrawer'
import { useAddressNotification } from '../../hooks/useAddressNotification'
import classes from './styles.module.css'

type AddressCardProps = BoxProps & {
  address: CoreUserAddress
}

export function AddressCard({ address, ...props }: AddressCardProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_locations' })
  const { mutate, isLoading } = useMutationUpdateUserAddress()
  const { openDrawer } = useAddressDrawer({ addressId: address.id })
  const getFlag = useAddressCountryFlag()
  const { showNotification } = useAddressNotification()
  const { refetch } = useQueryGetUserAddressById(
    {
      addressId: address.id ?? '',
    },
    { enabled: false }
  )

  const setMainAddress = () => {
    mutate(
      { addressId: address.id, address: { main: true } },
      { onSuccess: () => showNotification() }
    )
  }

  const fetchBeforeOpening = async () => {
    await refetch()
    openDrawer()
  }

  return (
    <Card {...props}>
      <Box className={classes.content}>
        <ThemeIcon variant='light' color='gray' className={classes.countryIcon}>
          <Text size='xl'>{getFlag(address.address_country_code)}</Text>
        </ThemeIcon>
        <Box className={classes.address}>
          <Text fw={500} truncate>
            {address.label}
          </Text>
          <TextAddress address={address} withIcon={false} />
        </Box>
        <Box className={classes.actions}>
          {address.main && <Badge color='blue'>{t('main_address')}</Badge>}
          <Menu position='bottom-end' withinPortal>
            <Menu.Target>
              <ActionIcon variant='default' loading={isLoading}>
                <Icon icon={faEllipsisVertical} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => fetchBeforeOpening()}>
                {t('actions.edit_address')}
              </Menu.Item>
              <Menu.Item
                disabled={address.main}
                onClick={() => setMainAddress()}
              >
                {t('actions.set_as_main_address')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      </Box>
    </Card>
  )
}
